import { useCallback, useEffect, useMemo, useState } from "react";
import { scaleTime } from "d3-scale";
import dayjs from "dayjs";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { ExploreAggColumnType } from "@/v2/entities/explore/columns/ExploreColum.model";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { ExploreInteractionType } from "@/v2/entities/explore/Explore.model";
import {
  ExploreChartEmptyState,
  ExploreChartLoader,
  getAggColumnConfig,
  normalizeValue,
} from "./explore-widgets-utils";
import { Tabs } from "@/v2/components/tabs/Tabs";
import { Flex } from "@radix-ui/themes";
import { useExploreDistributionChart } from "./useExploreDistributionChart";
import { TruncatedTick } from "./TruncatedTick";

export function useExploreOverTimeTimeGrouping() {
  const [timeGrouping, setTimeGrouping] = useState<"week" | "day" | "month">(
    "week"
  );

  const handleTimeGroupingChange = useCallback((value: string) => {
    if (value === "month") {
      setTimeGrouping("month");
    } else if (value === "week") {
      setTimeGrouping("week");
    } else {
      setTimeGrouping("day");
    }
  }, []);

  return {
    timeGrouping,
    handleTimeGroupingChange,
  };
}

interface ExploreOverTimeTabsProps {
  value: string;
  onValueChange: (value: string) => void;
}

export function ExploreOverTimeTabs({
  value,
  onValueChange,
}: ExploreOverTimeTabsProps) {
  return (
    <Tabs
      variant="data"
      value={value}
      onValueChange={onValueChange}
      tabs={[
        { value: "week", label: "Week" },
        { value: "day", label: "Day" },
        { value: "month", label: "Month" },
      ]}
    />
  );
}

interface ExploreOverTimeProps {
  source?: ExploreInteractionType;
  filter?: Filter;
  groupBy?: "week" | "day" | "month";
  splitBy?: string;
  metric?: ExploreAggColumnType;
  variant?: "area" | "line";
  hideValues?: string[];
}

function useExploreOverTimeValues({
  source,
  filter,
  groupBy = "week",
  splitBy,
  metric = "volume",
}: ExploreOverTimeProps) {
  const [selectedGroupBy, setSelectedGroupBy] = useState<string>(groupBy);

  const { items, isLoading, chartConfig, valueMap } =
    useExploreDistributionChart(
      source,
      selectedGroupBy,
      splitBy,
      metric,
      filter
    );

  const { xDomain, ticks, formatDate } = useMemo(() => {
    // Determine the minimum and maximum timestamps from the data.
    const timestamps: number[] = items.map((item) =>
      new Date(item.value).getTime()
    );

    const minTimestamp: number = Math.min(...timestamps);
    const maxTimestamp: number = Math.max(...timestamps);
    const xDomain = [minTimestamp, maxTimestamp];

    const dateFormat =
      dayjs.duration(maxTimestamp - minTimestamp).asYears() > 1
        ? "MMM DD"
        : "MMM DD";

    const formatDate = (timestamp: number): string =>
      dayjs(timestamp).format(dateFormat);

    if (selectedGroupBy === "week") {
      const ticks: number[] = timestamps.sort((a, b) => a - b);
      return { chartConfig, xDomain, ticks, formatDate };
    }

    // Create a D3 time scale using the computed min and max timestamps.
    // This scale is used here to generate tick values.
    const timeScale = scaleTime()
      .domain([new Date(minTimestamp), new Date(maxTimestamp)])
      .range([minTimestamp, maxTimestamp]);

    // Generate tick values (displaying 5 ticks on the x-axis).
    const ticks: number[] = timeScale.ticks(12).map((date) => date.getTime());

    return { chartConfig, xDomain, ticks, formatDate };
  }, [items]);

  useEffect(() => {
    setSelectedGroupBy(groupBy);
  }, [groupBy]);

  return {
    items,
    chartConfig,
    valueMap,
    xDomain,
    ticks,
    formatDate,
    isLoading,
    selectedGroupBy,
    setSelectedGroupBy,
  };
}

export function ExploreOverTime({
  source,
  filter,
  groupBy,
  splitBy,
  metric = "volume",
  variant = "area",
  hideValues = [],
}: ExploreOverTimeProps) {
  const {
    items,
    chartConfig,
    valueMap,
    xDomain,
    ticks,
    formatDate,
    isLoading,
    selectedGroupBy,
    setSelectedGroupBy,
  } = useExploreOverTimeValues({ source, splitBy, metric, filter, groupBy });
  const chartData = useMemo(() => {
    return items.map(({ value, values, ...rest }) => ({
      ...rest,
      timestamp: new Date(value).getTime(),
      ...Object.fromEntries(values.map(([key, value]) => [key, value])),
    }));
  }, [items]);

  const {
    valueFormatter: valueFormatter,
    labelFormatter: yTickLabelFormatter,
    footerLabel,
    footerValueFormatter,
    domain: yDomain,
    shouldStack,
  } = useMemo(() => {
    return getAggColumnConfig(metric);
  }, [metric]);

  const footerFormatter = useMemo(() => {
    return (payload: any) => {
      const metricValue = payload[0].payload[normalizeValue(metric)];

      if (typeof metricValue === "number") {
        return footerValueFormatter?.(metricValue);
      }

      return metricValue;
    };
  }, [footerValueFormatter, metric]);

  const tooltipLabelGetter = useMemo(() => {
    return ({ timestamp }: { timestamp: number }) => {
      if (selectedGroupBy === "week") {
        const startOfWeek = dayjs(timestamp).startOf("week");
        const endOfWeek = dayjs(timestamp).endOf("week");

        return `${formatDate(startOfWeek.toDate().getTime())} - ${formatDate(
          endOfWeek.toDate().getTime()
        )}`;
      }

      if (selectedGroupBy === "month") {
        const startOfMonth = dayjs(timestamp).startOf("month");
        const endOfMonth = dayjs(timestamp).endOf("month");

        return `${formatDate(startOfMonth.toDate().getTime())} - ${formatDate(
          endOfMonth.toDate().getTime()
        )}`;
      }

      return formatDate(timestamp);
    };
  }, [formatDate, selectedGroupBy]);

  if (isLoading) {
    return <ExploreChartLoader />;
  }

  if (items.length === 0) {
    return <ExploreChartEmptyState variant="no-data" />;
  }

  return (
    <Flex direction="column" gap="2" className="w-full">
      {groupBy == null && (
        <Flex p="4" justify="end">
          <ExploreOverTimeTabs
            value={selectedGroupBy}
            onValueChange={setSelectedGroupBy}
          />
        </Flex>
      )}

      <Flex flexGrow={"1"} height="0">
        <ChartContainer config={chartConfig} className="w-full h-full">
          <AreaChart
            data={chartData}
            margin={{ left: 8, top: 8, right: 8, bottom: 8 }}
          >
            <defs>
              {Array.from(valueMap.keys()).map((value) => (
                <linearGradient
                  key={value}
                  id={`fill${value}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={`var(--color-${value})`}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={`var(--color-${value})`}
                    stopOpacity={0.1}
                  />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="timestamp"
              type="number"
              domain={xDomain}
              ticks={ticks}
              tickFormatter={formatDate}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
            />
            <YAxis
              interval={0}
              minTickGap={6}
              tickLine={false}
              axisLine={false}
              height={48}
              domain={yDomain}
              tick={
                <TruncatedTick
                  axis="y"
                  anchor="middle"
                  dx={-16}
                  slotWidth={64}
                  pad={4}
                  labelFormatter={yTickLabelFormatter}
                />
              }
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  valueFormatter={(value) =>
                    typeof value === "number" ? valueFormatter?.(value) : value
                  }
                  labelGetter={tooltipLabelGetter}
                  footerFormatter={footerFormatter}
                  footerLabel={footerLabel}
                  indicator="dot"
                  hideValues={hideValues}
                />
              }
            />
            {Array.from(valueMap.keys()).map((value) => (
              <Area
                key={value}
                dataKey={value}
                type={variant === "area" ? "natural" : "linear"}
                unit={metric}
                fill={variant === "area" ? `url(#fill${value})` : "transparent"}
                stroke={`var(--color-${value})`}
                stackId={shouldStack ? "a" : undefined}
                fillOpacity={hideValues.includes(value) ? 0 : 1}
                strokeOpacity={hideValues.includes(value) ? 0 : 1}
                activeDot={!hideValues.includes(value)}
              />
            ))}
            <ChartLegend
              content={<ChartLegendContent hideValues={hideValues} />}
            />
          </AreaChart>
        </ChartContainer>
      </Flex>
    </Flex>
  );
}
